/**
 * AimSelectiveMenuDialog — React port of
 * `frmDietech_AutoItemModification_SMChanges.vb`.
 *
 * Shown after the diet-order AIM cascade's MP/Adjustment leg
 * completes when `GenerateSMChanges` returned proposals (see
 * `useAimSelectiveMenuRun`). Two-pane layout:
 *
 *   • Left  — one row per (MenuDate, Mealtime) combo that will
 *             change. Clicking a row filters the right pane.
 *   • Right — proposal grid for the focused day/mealtime with
 *             Original / ✓ / New columns. User toggles the
 *             checkbox column to accept or reject each row.
 *
 * Save → `useAimSelectiveMenuResolve` (calls SaveSMChanges).
 * Cancel → 2-tier prompt:
 *   1. "Save the new selective menus?" (Yes/No/Cancel)
 *      • Yes → same Save path.
 *      • No  → step 2.
 *      • Cancel → stay on dialog.
 *   2. "Revert to originals?" (Yes/No)
 *      • Yes → `useAimSelectiveMenuRevertOld` (re-inserts the
 *              pre-AIM snapshot the engine had already deleted).
 *      • No  → stay on dialog (DB is in a partial state
 *              otherwise — WinForms parity: prevent close).
 *
 * v1 deferrals (per user):
 *   • No Open/edit-item sub-dialog (checkbox + Push only).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DialogActionsBar from '../../components/shared/DialogActionsBar';
import { SECONDARY_BUTTON_SX } from '../../components/shared/dialogActionsSx';
import { useNotificationStore } from '../../stores/notificationStore';
import { useGlobalStore } from '../../stores/globalStore';
import {
  useAimSelectiveMenuResolve,
  useAimSelectiveMenuResolveDashboard,
  useAimSelectiveMenuRevertOld,
  type AimSMRunResponse,
  type AimSMRowDict,
} from '../../api/aimSelectiveMenuApi';
import { useQueueReport } from '../../api/reportApi';
import { useFacilityDate } from '../../hooks/useFacilityDate';
import { smChangesReportRequest } from './smChangesReport';
import './AimSelectiveMenuDialog.css';

interface Props {
  open: boolean;
  facilityId: number;
  patientId: number;
  patientFullName: string;
  result: AimSMRunResponse | null;
  /** True while `aimSelectiveMenuRun.mutateAsync(...)` is in
   *  flight. When set, the dialog mounts in a spinner state so
   *  the MUI backdrop blocks page interaction (nav clicks,
   *  form edits, other AIM triggers) while the engine runs.
   *  Mirrors `<AimDietUpdateDialog loading>` from the MP/Adj
   *  leg — user has already seen this exact pattern seconds
   *  earlier and expects it. */
  loading?: boolean;
  /** Dashboard "Auto Generated Selective Menus" viewer mode.
   *  Mirrors WinForms `frmDietech_AutoItemModification_SMChanges._isDashboard`
   *  — enables the per-mealtime lock icon in the New column header
   *  and skips the 2-tier revert Cancel prompt (nothing was pre-
   *  deleted on the historical path, so revert is inapplicable). */
  isDashboard?: boolean;
  /** Fully read-only: hide Save + Selected checkbox column + Push,
   *  rename Cancel → "OK". Set by the dashboard when every
   *  dayMealtime row has `ShowLock = true` (WinForms
   *  ucDashboard_SelectiveAutoGenerated.vb:405 — passes
   *  `dtDayMealtime.Select("ShowLock = FALSE").Count = 0`). */
  isReadOnly?: boolean;
  /** Fires after Save / Revert / user-confirmed discard.
   *  Parent should clear its state and drain any queued nav. */
  onClose: () => void;
}

// Shared style for toolbar action buttons — small outlined
// buttons that fit inside the 40px toolbar row without pushing
// its height (matches the Diet-Order-Changed compact button
// pattern in ResidentDetail).
const toolbarButtonSx = {
  textTransform: 'none' as const,
  height: 28,
  minHeight: 28,
  py: 0,
  px: 1.25,
  fontSize: 12,
  lineHeight: 1,
  '& .MuiButton-startIcon': { mr: 0.5, ml: -0.25 },
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const bool = (v: unknown): boolean => v === true || v === 1 || v === '1' || v === 'true';
const str = (v: unknown): string => (v == null ? '' : String(v));

// Dictionary rows arrive from the API with keys transformed by
// `FirstCharLowerPolicy` (Program.cs:422) — e.g. `MenuDate` →
// `menuDate`, `Position_New` → `position_New`,
// `FKey_Prd_Selective_Menu` → `fKey_Prd_Selective_Menu`. To stay
// agnostic to policy changes AND to survive the couple of columns
// that already ship all-lowercase (SQL Server casing quirks),
// look up the key case-insensitively. Cached per-row so the scan
// only happens once per unique row object.
const KEY_CACHE = new WeakMap<AimSMRowDict, Map<string, string>>();
function findKey(row: AimSMRowDict | undefined | null, key: string): string | undefined {
  if (!row) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return key;
  const lc = key.charAt(0).toLowerCase() + key.slice(1);
  if (Object.prototype.hasOwnProperty.call(row, lc)) return lc;
  let cache = KEY_CACHE.get(row);
  if (!cache) {
    cache = new Map();
    for (const k of Object.keys(row)) cache.set(k.toLowerCase(), k);
    KEY_CACHE.set(row, cache);
  }
  return cache.get(key.toLowerCase());
}
/** Case-insensitive field read. Returns the raw value or undefined. */
const f = (row: AimSMRowDict | undefined | null, key: string): unknown => {
  const k = findKey(row, key);
  return k && row ? row[k] : undefined;
};
/** Case-insensitive field write. Preserves the row's existing key
 *  casing when one is present; otherwise falls back to the
 *  first-char-lower form (matches how the server ships new rows). */
function setF(row: AimSMRowDict, key: string, value: unknown): void {
  const k = findKey(row, key) ?? (key.charAt(0).toLowerCase() + key.slice(1));
  row[k] = value;
}

export default function AimSelectiveMenuDialog({ open, facilityId, patientId, patientFullName, result, loading, isDashboard, isReadOnly, onClose }: Props) {
  const addNotification = useNotificationStore((s) => s.addNotification);
  const breakfastName = useGlobalStore((s) => s.breakfastName);
  const lunchName = useGlobalStore((s) => s.lunchName);
  const dinnerName = useGlobalStore((s) => s.dinnerName);

  const resolveMutation = useAimSelectiveMenuResolve();
  const userId = useGlobalStore((s) => s.userId);
  const queueReport = useQueueReport(facilityId);
  const { today: facilityToday } = useFacilityDate();
  const resolveDashboardMutation = useAimSelectiveMenuResolveDashboard();
  const revertMutation = useAimSelectiveMenuRevertOld();
  // Dashboard save-back routes to a DIFFERENT endpoint that
  // rewrites tblPrd_Selective_Menu directly (WinForms
  // `SaveDataDashboard`), NOT the AIM cascade — the AIM cascade
  // would clobber SM history and re-run diet-order logic against
  // an already-applied change.
  const activeResolveMutation = isDashboard ? resolveDashboardMutation : resolveMutation;

  // Working copies of the 4 tables — we mutate `combined`'s
  // Selected flags in place, so keep them in state.
  const [dayMealtimes, setDayMealtimes] = useState<AimSMRowDict[]>([]);
  const [combined, setCombined] = useState<AimSMRowDict[]>([]);
  const [selectiveMenuOldRef] = useState<{ current: AimSMRowDict[] }>({ current: [] });
  const [selectiveMenuNewRef] = useState<{ current: AimSMRowDict[] }>({ current: [] });
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  // Row-level focus WITHIN the current day-mealtime's proposal
  // grid — mirrors WinForms `gv.FocusedRowHandle`. Toolbar Push
  // reads this to know which row to act on; row clicks set it.
  // Resets to 0 when the day-mealtime selection changes.
  const [focusedRowInMeal, setFocusedRowInMeal] = useState<number>(0);
  const dirtyRef = useRef(false);
  // 2-tier Cancel prompt state — 'save' asks whether to save
  // the new selections; 'revert' asks whether to restore the
  // originals that GenerateSMChanges deleted. Both render as
  // <ConfirmDialog> instead of window.confirm.
  const [cancelPrompt, setCancelPrompt] = useState<'none' | 'save' | 'revert'>('none');

  useEffect(() => {
    if (!result) return;
    // Deep-clone the combined rows so `Selected` toggles don't
    // reach into the mutation's cached response object.
    const cloneCombined = (result.combined ?? []).map((r) => ({ ...r }));
    // Sort day-mealtimes: MenuDate ASC, Mealtime ASC (matches
    // the WinForms sort at .vb:105-106).
    const sortedDays = [...(result.dayMealtimes ?? [])].sort((a, b) => {
      const da = str(f(a, 'MenuDate'));
      const db = str(f(b, 'MenuDate'));
      if (da !== db) return da < db ? -1 : 1;
      return (num(f(a, 'Mealtime')) ?? 0) - (num(f(b, 'Mealtime')) ?? 0);
    });
    setDayMealtimes(sortedDays);
    setCombined(cloneCombined);
    selectiveMenuOldRef.current = result.selectiveMenuOld ?? [];
    selectiveMenuNewRef.current = result.selectiveMenuNew ?? [];
    setFocusedIndex(0);
    setFocusedRowInMeal(0);
    dirtyRef.current = false;
  }, [result, selectiveMenuOldRef, selectiveMenuNewRef]);

  // Row-focus reset is handled explicitly at each focusedIndex
  // setter (left-pane click → 0, Next-not-found jump → 0,
  // Prev-not-found jump → last row). A `useEffect(focusedIndex)`
  // reset would race with the Prev jump's `setFocusedRowInMeal
  // (lastIdx)` call and clobber it.

  const mealtimeLabel = useCallback((mt: number | null): string => {
    if (mt === 0) return breakfastName;
    if (mt === 1) return lunchName;
    if (mt === 2) return dinnerName;
    return mt == null ? '' : String(mt);
  }, [breakfastName, lunchName, dinnerName]);

  const focusedDay = dayMealtimes[focusedIndex];
  const focusedDayKey = focusedDay
    ? { day: num(f(focusedDay, 'Day')), mealtime: num(f(focusedDay, 'Mealtime')) }
    : null;
  // ShowLock is populated by the server on the dashboard historical
  // path (SelectiveMenuChanges_Historical); on the diet-order AIM
  // path the column is absent and this stays false. Drives the New-
  // column lock icon AND the per-mealtime Push disable — mirrors
  // WinForms `_isDashboard AndAlso rowDayMealtime("ShowLock")` gates
  // at frmDietech_AutoItemModification_SMChanges.vb:830-848 + 1060.
  const focusedDayLocked = !!isDashboard && bool(f(focusedDay, 'ShowLock'));

  // Right grid = combined rows for the focused (Day, Mealtime).
  const focusedRows = useMemo(() => {
    if (!focusedDayKey) return [] as AimSMRowDict[];
    return combined.filter(
      (r) => num(f(r, 'Day')) === focusedDayKey.day && num(f(r, 'Mealtime')) === focusedDayKey.mealtime,
    );
  }, [combined, focusedDayKey]);

  // Column names mirror WinForms `FKey_ColumnName` and
  // `FKey_ColumnName_Detail` (swapped when `_isDashboard=True`
  // at frmDietech_AutoItemModification_SMChanges.vb:78-84).
  //
  // Note the counter-intuitive Dashboard mapping —
  //   FKey_ColumnName         → …_New (the applied-log FK)
  //   FKey_ColumnName_Detail  → …_Old (the pre-auto snapshot FK)
  // — the "New" side is what's PUSHABLE (the thing the user can
  // revert), the "Old" side is the DETAIL match. Prior mapping
  // treated `_Old` as the new-side FK and every Push/conflict
  // rule read the wrong column → button permanently disabled,
  // count/nav also wrong.
  const fkColumnName = isDashboard ? 'FKey_Log_Prd_Selective_Menu_New' : 'FKey_Prd_Selective_Menu';
  const fkColumnNameDetail = isDashboard ? 'FKey_Log_Prd_Selective_Menu_Old' : 'FKey_Prd_Menu_Detail';

  // Push toolbar-button enable — matches WinForms
  // `mbtnPushRight.Enabled` at .vb:841:
  //   (HighlightNewItem AND NOT IsNewMPItem) OR
  //    FKey_ColumnName IS NOT NULL
  const canPushFocused = useMemo(() => {
    const row = focusedRows[focusedRowInMeal];
    if (!row) return false;
    if (bool(f(row, 'HighlightNewItem')) && !bool(f(row, 'IsNewMPItem'))) return true;
    return f(row, fkColumnName) != null;
  }, [focusedRows, focusedRowInMeal, fkColumnName]);
  // Open toolbar-button enable — matches WinForms
  // `mbtnOpen.Enabled` at .vb:825:
  //   Position_New > 1 AND Selected = TRUE
  // (WinForms also gates on focused column being the New-descr
  // column; we don't have a column-focus concept per-cell yet
  // so just row-focus is enough.)
  const canOpenFocused = useMemo(() => {
    const row = focusedRows[focusedRowInMeal];
    if (!row) return false;
    return (num(f(row, 'Position_New')) ?? 0) > 1 && bool(f(row, 'Selected'));
  }, [focusedRows, focusedRowInMeal]);

  // Count = rows that read as "conflict" — matches WinForms
  // UpdateChangeCount at .vb:924. Uses the same isDashboard-
  // aware column pair as the Prev/Next scan below (isConflict).
  const changeCount = useMemo(() => {
    return combined.filter((r) => {
      const pkMain = f(r, fkColumnName);         // WinForms FKey_ColumnName
      const pkDetail = f(r, fkColumnNameDetail); // WinForms FKey_ColumnName_Detail
      const posNew = num(f(r, 'Position_New')) ?? 0;
      const selected = bool(f(r, 'Selected'));
      const oldDescr = str(f(r, 'Itm_Descr_Old'));
      const newDescr = str(f(r, 'Itm_Descr_New'));
      return (
        (pkDetail == null && !selected)
        || bool(f(r, 'HighlightNewItem'))
        // ADDED items — auto-process wrote a new SM row that had
        // no counterpart in the pre-auto snapshot. Marked with
        // `HighlightNewItemOnlySelected` in BuildCombinedTable
        // (both dashboard and AIM paths). WinForms's original
        // rule set missed these on the DASHBOARD side because
        // its rule 3 (`FKey_ColumnName IS NULL && selected`)
        // relies on the AIM-only convention where the "existing"
        // FK is null for added items — on dashboard, the "New"
        // FK is SET (that's what auto-process wrote), so rule 3
        // never fires. Adding this catches them explicitly.
        || (bool(f(r, 'HighlightNewItemOnlySelected')) && selected && posNew > 1)
        || (pkMain == null && posNew > 1 && selected)
        || (oldDescr !== '' && oldDescr !== newDescr)
      );
    }).length;
  }, [combined, fkColumnName, fkColumnNameDetail]);

  // Apply the parent/sibling/descendant Selected cascade around
  // `target` after it has just moved to `newValue`. Mirrors
  // WinForms `repChk_EditValueChanging` at .vb:720-800 verbatim.
  // MUTATES rows in `next` in place — the caller has already
  // cloned `next` from the previous state and set target.Selected
  // = newValue before invoking us.
  //
  // Layout of the four steps below matches the WinForms order:
  //   1. Position>1 + newValue=true → cascade UP the immediate
  //      sibling chain (positions strictly between focused and 2),
  //      setting them to newValue.
  //   2. Position>1 → find the first Position=1 ancestor above
  //      and set its Selected to `checkMC`: TRUE when we're
  //      turning ON, or when ANY Position=2 sibling in the same
  //      category is still selected; FALSE only when we're
  //      turning OFF and no such sibling is selected.
  //   3. newValue=true → walk up to the first Position=1 ancestor
  //      and force TRUE. Redundant with step 2 when posNew>1;
  //      matters when posNew=1 or as a safety belt.
  //   4. Cascade DOWN unconditionally: every descendant with
  //      Position_New > focused's, up until the chain breaks,
  //      gets newValue. Fixes the previous bug where unchecking
  //      a parent left children selected.
  const applySelectedCascade = useCallback((
    next: AimSMRowDict[],
    target: AimSMRowDict,
    newValue: boolean,
  ) => {
    const day = num(f(target, 'Day'));
    const mt = num(f(target, 'Mealtime'));
    const posNew = num(f(target, 'Position_New')) ?? 0;
    const category = num(f(target, 'FKey_Prd_Menu_Category'));
    const mealRows = next.filter(
      (r) => num(f(r, 'Day')) === day && num(f(r, 'Mealtime')) === mt,
    );
    const targetIdx = mealRows.indexOf(target);
    if (targetIdx < 0) return;

    if (posNew > 1) {
      // Step 1 — up-cascade to same-chain siblings (only on ON)
      if (newValue) {
        for (let i = targetIdx - 1; i >= 0; i--) {
          const p = num(f(mealRows[i], 'Position_New')) ?? 0;
          if (posNew < p || p === 1) break;
          if (posNew > p) setF(mealRows[i], 'Selected', newValue);
          if (p <= 2) break;
        }
      }
      // Step 2 — Position=1 ancestor gets `checkMC`
      let checkMC = true;
      if (!newValue) {
        checkMC = next.some((r) =>
          num(f(r, 'Day')) === day && num(f(r, 'Mealtime')) === mt
          && num(f(r, 'FKey_Prd_Menu_Category')) === category
          && num(f(r, 'Position_New')) === 2 && bool(f(r, 'Selected')));
      }
      for (let i = targetIdx - 1; i >= 0; i--) {
        const p = num(f(mealRows[i], 'Position_New')) ?? 0;
        if (p === 1) {
          setF(mealRows[i], 'Selected', checkMC);
          break;
        }
      }
    }
    // Step 3 — safety-belt: on ON, force nearest Position=1
    // ancestor TRUE (WinForms .vb:777-786, outside the posNew>1
    // guard).
    if (newValue) {
      for (let i = targetIdx - 1; i >= 0; i--) {
        const p = num(f(mealRows[i], 'Position_New')) ?? 0;
        if (p === 1) {
          setF(mealRows[i], 'Selected', newValue);
          break;
        }
      }
    }
    // Step 4 — down-cascade (ALWAYS, both ON and OFF)
    for (let i = targetIdx + 1; i < mealRows.length; i++) {
      const p = num(f(mealRows[i], 'Position_New')) ?? 0;
      if (posNew < p) setF(mealRows[i], 'Selected', newValue);
      else break;
    }
  }, []);

  // Toggle Selected on a row — user clicked the row's checkbox.
  // Sets Selected then runs the shared cascade.
  const toggleSelected = useCallback((rowIndex: number, nextValue: boolean) => {
    const targetRow = focusedRows[rowIndex];
    if (!targetRow) return;
    const targetKey = targetRow;
    setCombined((prev) => {
      const next = prev.map((r) => ({ ...r }));
      const target = next.find((r) => r === targetKey) ?? next.find((r) => sameRow(r, targetKey));
      if (!target) return prev;
      setF(target, 'Selected', nextValue);
      applySelectedCascade(next, target, nextValue);
      dirtyRef.current = true;
      return next;
    });
  }, [focusedRows, applySelectedCascade]);

  // Push button — full port of WinForms `ToggleSelectedProperties`
  // at frmDietech_AutoItemModification_SMChanges.vb:934-983.
  // Two branches (matches enable rule at .vb:841):
  //
  //   Branch 1 — HighlightNewItem AND NOT IsNewMPItem:
  //     • Mirror text/flags on the combined row (descr, clear
  //       highlight, force Selected=true, clear all AIM_*).
  //     • Cross-table FK repair: look up `rowOld` in
  //       tblSelectiveMenu_Old by FKey_Prd_Selective_Menu.
  //       Pick `rowToUse` (tblSelectiveMenu_New match if
  //       FKey_Prd_Menu_Detail is non-null, else rowOld). Copy
  //       6 FKs from rowOld INTO rowToUse. Without this, Save
  //       would insert the NEW menu's item instead of the
  //       original the user asked to keep — the tblCombined
  //       row alone doesn't carry those FKs; Save reads them
  //       from rowToUse.
  //     • Fire cascade as (False→True) — mirrors WinForms
  //       repChk_EditValueChanging(False, True) call at .vb:971.
  //
  //   Branch 2 — FKey_Prd_Selective_Menu IS NOT NULL (no highlight):
  //     • Flip Selected in place.
  //     • Fire cascade with the new value — WinForms
  //       repChk_EditValueChanging(!newSel, newSel) at .vb:976
  //       propagates through parents/siblings/descendants.
  const pushToLeft = useCallback((rowIndex: number) => {
    const targetRow = focusedRows[rowIndex];
    if (!targetRow) return;
    const isBranch1 = bool(f(targetRow, 'HighlightNewItem')) && !bool(f(targetRow, 'IsNewMPItem'));
    const isBranch2 = !isBranch1 && f(targetRow, fkColumnName) != null;
    if (!isBranch1 && !isBranch2) return;

    setCombined((prev) => {
      const next = prev.map((r) => ({ ...r }));
      const target = next.find((r) => sameRow(r, targetRow));
      if (!target) return prev;

      if (isBranch1) {
        // — Combined-row updates —
        setF(target, 'Itm_Descr_New', f(target, 'Itm_Descr_Old'));
        setF(target, 'HighlightNewItem', false);
        setF(target, 'Selected', true);
        if (!isDashboard) {
          setF(target, 'AIM_Valid', false);
          setF(target, 'AIM_FKey_Item', null);
          setF(target, 'AIM_FKey_Itm_Size', null);
          setF(target, 'AIM_FKey_Consist', null);
          setF(target, 'AIM_FKey_Item_Prd_ItemType', null);
          setF(target, 'AIM_FKey_Prd_Service_Item', null);
          setF(target, 'AIM_FKey_Prd_Portion', null);
        }

        // — Cross-table FK repair —
        // Ref arrays are held on state — mutating rows in place
        // is fine because we send them right back to the server
        // on Resolve. React re-render is not needed since the
        // grid renders off `combined`, not the refs.
        // Dashboard (historical) rows use the log tables' names: the
        // combined row points at the old and new log rows through
        // FKey_Log_Prd_Selective_Menu_Old / _New, both log tables are keyed
        // by PKey_Log_Prd_Selective_Menu and their columns carry the
        // Prd_Selective_Menu_ prefix (WinForms SMChanges ctor .vb:79-84).
        // The row patched is the one Save inserts: SaveHistorical reads the
        // _New log row when the combined row has one, else the _Old row
        // (SelectiveMenuChanges_Historical.vb:567-576).
        const day = num(f(target, 'Day'));
        const mt = num(f(target, 'Mealtime'));
        let rowOld: AimSMRowDict | undefined;
        let rowToUse: AimSMRowDict | undefined;
        const prefix = isDashboard ? 'Prd_Selective_Menu_' : '';
        if (isDashboard) {
          const oldPk = num(f(target, 'FKey_Log_Prd_Selective_Menu_Old'));
          const newPk = num(f(target, 'FKey_Log_Prd_Selective_Menu_New'));
          rowOld = selectiveMenuOldRef.current.find(
            (r) => num(f(r, 'PKey_Log_Prd_Selective_Menu')) === oldPk,
          );
          rowToUse = newPk == null ? rowOld : selectiveMenuNewRef.current.find(
            (r) => num(f(r, 'PKey_Log_Prd_Selective_Menu')) === newPk
              && num(f(r, `${prefix}Day`)) === day
              && num(f(r, `${prefix}Mealtime`)) === mt,
          );
        } else {
          const fkOldPk = num(f(target, 'FKey_Prd_Selective_Menu'));
          const fkDetail = num(f(target, 'FKey_Prd_Menu_Detail'));
          rowOld = selectiveMenuOldRef.current.find(
            (r) => num(f(r, 'FKey_Prd_Selective_Menu')) === fkOldPk,
          );
          rowToUse = rowOld;
          if (fkDetail != null) {
            rowToUse = selectiveMenuNewRef.current.find(
              (r) => num(f(r, 'PKey_Prd_Menu_Detail')) === fkDetail
                && num(f(r, 'Day')) === day
                && num(f(r, 'Mealtime')) === mt,
            );
          }
        }
        if (rowOld && rowToUse) {
          for (const col of [
            'FKey_Item', 'FKey_Consist', 'FKey_Item_Prd_ItemType',
            'FKey_Itm_Size', 'FKey_Prd_Service_Item', 'FKey_Prd_Portion',
          ]) {
            setF(rowToUse, `${prefix}${col}`, f(rowOld, `${prefix}${col}`));
          }
        }

        applySelectedCascade(next, target, true);
      } else {
        // Branch 2 — flip Selected, cascade with the new value.
        const flipped = !bool(f(target, 'Selected'));
        setF(target, 'Selected', flipped);
        applySelectedCascade(next, target, flipped);
      }
      dirtyRef.current = true;
      return next;
    });
  }, [focusedRows, applySelectedCascade, selectiveMenuOldRef, selectiveMenuNewRef, isDashboard, fkColumnName]);

  // Navigate to the next / previous conflict row across all
  // day-mealtimes (not just the focused one). Same "conflict"
  // definition as `changeCount`. Uses the same isDashboard-aware
  // `oldFkColumn`/`newFkColumn` pair declared at the top of the
  // component — reading the AIM-flow FKs on a historical row
  // returns undefined everywhere and every non-selected row
  // registers as a conflict, so Next just walks one row at a time.
  const isConflict = useCallback((r: AimSMRowDict): boolean => {
    const pkMain = f(r, fkColumnName);
    const pkDetail = f(r, fkColumnNameDetail);
    const posNew = num(f(r, 'Position_New')) ?? 0;
    const selected = bool(f(r, 'Selected'));
    const oldDescr = str(f(r, 'Itm_Descr_Old'));
    const newDescr = str(f(r, 'Itm_Descr_New'));
    return (
      (pkDetail == null && !selected && posNew > 1)
      || bool(f(r, 'HighlightNewItem'))
      // See changeCount — added items on the dashboard path have
      // FKey_ColumnName SET (New log entry), so WinForms rule 3
      // misses them. `HighlightNewItemOnlySelected` is set at
      // BuildCombinedTable for every added-and-selected row.
      || (bool(f(r, 'HighlightNewItemOnlySelected')) && selected && posNew > 1)
      || (pkMain == null && posNew > 1 && selected)
      || (oldDescr !== '' && oldDescr !== newDescr)
    );
  }, [fkColumnName, fkColumnNameDetail]);
  // Prev/Next enable — true when at least one conflict row
  // exists in the corresponding direction, across ALL day-
  // mealtimes (not just the current one). Once the user has
  // navigated past the last conflict in a direction the button
  // greys out. Not a WinForms parity feature — those buttons
  // stay enabled always — but requested UX improvement.
  const rowsForDay = useCallback((day: AimSMRowDict) => (
    combined.filter(
      (r) => num(f(r, 'Day')) === num(f(day, 'Day')) && num(f(r, 'Mealtime')) === num(f(day, 'Mealtime')),
    )
  ), [combined]);
  const canGoPrev = useMemo(() => {
    for (let i = focusedRowInMeal - 1; i >= 0; i--) {
      if (isConflict(focusedRows[i])) return true;
    }
    for (let d = focusedIndex - 1; d >= 0; d--) {
      if (rowsForDay(dayMealtimes[d]).some(isConflict)) return true;
    }
    return false;
  }, [focusedRows, focusedRowInMeal, focusedIndex, dayMealtimes, isConflict, rowsForDay]);
  const canGoNext = useMemo(() => {
    for (let i = focusedRowInMeal + 1; i < focusedRows.length; i++) {
      if (isConflict(focusedRows[i])) return true;
    }
    for (let d = focusedIndex + 1; d < dayMealtimes.length; d++) {
      if (rowsForDay(dayMealtimes[d]).some(isConflict)) return true;
    }
    return false;
  }, [focusedRows, focusedRowInMeal, focusedIndex, dayMealtimes, isConflict, rowsForDay]);
  // Prev/Next change — port of WinForms `GoToNextConflict`
  // at .vb:218-302. Two-phase behavior:
  //
  //   Phase 1 — scan within the CURRENT day-mealtime:
  //     • Prev (direction=-1): walk focusedRow-1 down to 0
  //     • Next (direction=+1): walk focusedRow+1 up to end
  //     If any row matches `isConflict`, focus it and return.
  //
  //   Phase 2 — no conflict found in this mealtime:
  //     • Prev: hop to previous day-mealtime, land on its LAST
  //             row (WinForms `_startAtBottomOnDayChange = True`
  //             → `gvDetail.FocusedRowHandle = gvDetail.RowCount - 1`).
  //             User clicks Prev again to actually find the
  //             next conflict there — same as WinForms.
  //     • Next: hop to next day-mealtime, land on FIRST row (0).
  //     If already at the boundary (index 0 for Prev / last for
  //     Next), no-op — WinForms guards with the same bounds.
  const jumpToNextConflict = useCallback((direction: 1 | -1) => {
    if (dayMealtimes.length === 0) return;
    // Phase 1 — within-mealtime scan
    if (direction === -1) {
      for (let i = focusedRowInMeal - 1; i >= 0; i--) {
        if (isConflict(focusedRows[i])) {
          setFocusedRowInMeal(i);
          return;
        }
      }
      // Phase 2 — hop to previous day-mealtime, land on LAST row
      if (focusedIndex > 0) {
        const newIdx = focusedIndex - 1;
        const newDay = dayMealtimes[newIdx];
        const newRowCount = combined.filter(
          (r) => num(f(r, 'Day')) === num(f(newDay, 'Day')) && num(f(r, 'Mealtime')) === num(f(newDay, 'Mealtime')),
        ).length;
        setFocusedIndex(newIdx);
        setFocusedRowInMeal(Math.max(0, newRowCount - 1));
      }
    } else {
      for (let i = focusedRowInMeal + 1; i < focusedRows.length; i++) {
        if (isConflict(focusedRows[i])) {
          setFocusedRowInMeal(i);
          return;
        }
      }
      // Phase 2 — hop to next day-mealtime, land on FIRST row
      if (focusedIndex < dayMealtimes.length - 1) {
        setFocusedIndex(focusedIndex + 1);
        setFocusedRowInMeal(0);
      }
    }
  }, [combined, dayMealtimes, focusedIndex, focusedRowInMeal, focusedRows, isConflict]);

  const submitSave = useCallback(async (): Promise<boolean> => {
    try {
      await activeResolveMutation.mutateAsync({
        facilityId,
        patientId,
        dayMealtimes,
        combined,
        selectiveMenuOld: selectiveMenuOldRef.current,
        selectiveMenuNew: selectiveMenuNewRef.current,
      });
      addNotification({
        message: 'Selective menu changes saved.',
        type: 'success',
        duration: 6000,
      });
      return true;
    } catch (err) {
      addNotification({
        message: `Failed to save selective menu changes: ${err instanceof Error ? err.message : String(err)}`,
        type: 'error',
        duration: 10000,
      });
      return false;
    }
  }, [activeResolveMutation, facilityId, patientId, dayMealtimes, combined, selectiveMenuOldRef, selectiveMenuNewRef, addNotification]);

  const submitRevert = useCallback(async (): Promise<boolean> => {
    try {
      await revertMutation.mutateAsync({
        facilityId,
        patientId,
        selectiveMenuOld: selectiveMenuOldRef.current,
      });
      addNotification({
        message: 'Original selective menu choices restored.',
        type: 'info',
        duration: 6000,
      });
      return true;
    } catch (err) {
      addNotification({
        message: `Failed to restore original selective menu choices: ${err instanceof Error ? err.message : String(err)}`,
        type: 'error',
        duration: 10000,
      });
      return false;
    }
  }, [revertMutation, facilityId, patientId, selectiveMenuOldRef, addNotification]);

  // 2-tier Cancel flow — matches WinForms FormClosing at
  // .vb:672-708. Opens the "save?" ConfirmDialog; the dialog's
  // own handlers advance the state machine.
  //
  // Dashboard + read-only paths skip the prompt entirely and
  // just close — WinForms `_isDashboard OrElse _saveInProgress`
  // short-circuits at .vb:675, and there's nothing pre-deleted
  // to revert on the historical viewer path.
  const handleCancel = useCallback(() => {
    if (isDashboard || isReadOnly) {
      onClose();
      return;
    }
    setCancelPrompt('save');
  }, [isDashboard, isReadOnly, onClose]);

  // Save prompt → Save clicked. Persist and close on success;
  // stay open on failure so the user can retry.
  const handleSaveFromPrompt = useCallback(async () => {
    const ok = await submitSave();
    if (ok) {
      setCancelPrompt('none');
      onClose();
    } else {
      setCancelPrompt('none');
    }
  }, [submitSave, onClose]);

  // Save prompt → No clicked. Advance to the revert prompt so
  // the user can restore the originals that GenerateSMChanges
  // already deleted from the DB. Mirrors WinForms DialogResult.No
  // arm at .vb:683-704.
  const handleDiscardFromSavePrompt = useCallback(() => {
    setCancelPrompt('revert');
  }, []);

  // Save prompt → Cancel clicked (or backdrop/escape). Return to
  // the SM dialog with everything intact — WinForms sets
  // `e.Cancel = True` at .vb:706 to keep the form open.
  const handleCancelSavePrompt = useCallback(() => {
    setCancelPrompt('none');
  }, []);

  // Revert prompt → Revert clicked. Re-insert selectiveMenuOld
  // rows and close on success.
  const handleRevertFromPrompt = useCallback(async () => {
    const ok = await submitRevert();
    if (ok) {
      setCancelPrompt('none');
      onClose();
    } else {
      setCancelPrompt('none');
    }
  }, [submitRevert, onClose]);

  // Revert prompt → Cancel clicked (or backdrop/escape). Stay
  // on the SM dialog — DB is in a partial state (originals
  // already deleted, nothing re-inserted), matching WinForms
  // behavior of preventing close without an explicit action.
  const handleCancelRevertPrompt = useCallback(() => {
    setCancelPrompt('none');
  }, []);

  const handleSave = useCallback(async () => {
    const ok = await submitSave();
    if (ok) onClose();
  }, [submitSave, onClose]);

  const isPending = activeResolveMutation.isPending || revertMutation.isPending;
  // Dialog states — mirrors AimDietUpdateDialog:
  //   1. !open              → null (unmounted)
  //   2. loading || !result → spinner state (engine is running,
  //                           user can't do anything else)
  //   3. open && result     → review grid (default rendering)
  const isAnalyzing = loading || result == null;

  return (
    <>
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // Nothing to close while analyzing — swallow backdrop
        // and Escape so the page-block is absolute until the
        // engine returns.
        if (isAnalyzing) return;
        // Block backdrop/escape closes while a mutation is in
        // flight, and always force the 2-tier prompt so users
        // can't dodge the revert flow by clicking outside.
        if (isPending) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleCancel();
        }
      }}
      maxWidth={false}
      slotProps={{
        paper: { sx: { width: '92vw', height: '90vh', display: 'flex', flexDirection: 'column' } },
      }}
    >
      <DialogTitle sx={{ py: 1, px: 2, borderBottom: 1, borderColor: 'divider', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ flex: 1 }}>
          {isAnalyzing
            ? 'Analyzing selective menu changes…'
            : `${patientFullName ? patientFullName + ' · ' : ''}Selective Menu Changes…`}
        </span>
        {/* Close button — runs the same 2-tier Cancel prompt as
            the footer Cancel / backdrop / Escape. Hidden while
            analyzing (nothing to cancel yet); disabled while a
            mutation is in flight. */}
        {!isAnalyzing && (
          <Tooltip title="Close">
            <span>
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={isPending}
                aria-label="close"
                sx={{ p: 0.25 }}
              >
                <i className="bi bi-x-lg" style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </DialogTitle>
      {isAnalyzing && (
        <DialogContent
          sx={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            color: 'var(--text-secondary)',
          }}
        >
          <CircularProgress size={48} />
          <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 500, color: 'var(--text-primary)' }}>
            Analyzing selective menu changes…
          </div>
          <div style={{ fontSize: 'var(--font-size-small)', textAlign: 'center', maxWidth: 460 }}>
            The auto item modification rules are evaluating every entered
            selective menu against the new diet order. This usually takes a
            few seconds.
          </div>
        </DialogContent>
      )}
      {!isAnalyzing && (
      <>
      {/* Toolbar row — [Open] | Count · [Prev] [Next] [Push].
          Layout mirrors WinForms `bar1` at the top of
          frmDietech_AutoItemModification_SMChanges.Designer.vb —
          Open on the far left (wires to per-row edit dialog,
          coming next), a break/divider, then the change
          navigation + push cluster. */}
      <div className="aim-sm-toolbar">
        {/* Open — placeholder onClick until Open dialog is
            wired. Enabled when the focused row has Position_New
            > 1 AND is Selected (matches WinForms .vb:825
            `mbtnOpen.Enabled` gate). */}
        {!isReadOnly && (
          <>
            <Tooltip title="Open item detail for edit (coming next)">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canOpenFocused || focusedDayLocked}
                  onClick={() => { /* wire-up pending */ }}
                  startIcon={<i className="bi bi-pencil-square" style={{ fontSize: 13 }} />}
                  sx={toolbarButtonSx}
                >
                  Open
                </Button>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          </>
        )}
        <span className="aim-sm-count">Count: ({changeCount})</span>
        <Tooltip title={canGoPrev ? 'Previous change' : 'No earlier changes'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={!canGoPrev}
              onClick={() => jumpToNextConflict(-1)}
              startIcon={<i className="bi bi-arrow-up" style={{ fontSize: 13 }} />}
              sx={toolbarButtonSx}
            >
              Previous
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={canGoNext ? 'Next change' : 'No further changes'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={!canGoNext}
              onClick={() => jumpToNextConflict(1)}
              startIcon={<i className="bi bi-arrow-down" style={{ fontSize: 13 }} />}
              sx={toolbarButtonSx}
            >
              Next
            </Button>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        {/* Push — WinForms `mbtnPushRight`. Operates on the
            focused row; enable rule matches WinForms .vb:841
            (`canPushFocused` above). Fires the same 2-branch
            logic as pushToLeft — branch 1 (highlighted conflict)
            mirrors old to new + copies FKs to the source row;
            branch 2 (kept row) flips Selected + cascades. */}
        {!isReadOnly && (
          <Tooltip title="Push original selection to the right (revert AIM for the focused row)">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!canPushFocused || focusedDayLocked}
                onClick={() => pushToLeft(focusedRowInMeal)}
                startIcon={<i className="bi bi-arrow-right-square" style={{ fontSize: 13 }} />}
                sx={toolbarButtonSx}
              >
                Push
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
      <DialogContent sx={{ display: 'flex', flex: 1, minHeight: 0, p: 0 }}>
        {/* Left pane */}
        <div className="aim-sm-left">
          <div className="aim-sm-left-header">
            <span>Date</span>
            <span>Mealtime</span>
          </div>
          <div className="aim-sm-left-body">
            {dayMealtimes.map((row, i) => (
              <div
                key={i}
                className={`aim-sm-left-row${i === focusedIndex ? ' focused' : ''}`}
                onClick={() => {
                  // Land on row 0 of the newly-picked mealtime
                  // (WinForms .vb:1080 else-branch). Matches the
                  // default `_startAtBottomOnDayChange = False`
                  // path.
                  setFocusedIndex(i);
                  setFocusedRowInMeal(0);
                }}
              >
                <div className="aim-sm-left-row-main">
                  <span>{formatDate(f(row, 'MenuDate'))}</span>
                  <span>{mealtimeLabel(num(f(row, 'Mealtime')))}</span>
                </div>
                {str(f(row, 'MenuDisplayString')) && (
                  <div className="aim-sm-left-row-sub">{str(f(row, 'MenuDisplayString'))}</div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Right pane */}
        <div className="aim-sm-right">
          <div className="aim-sm-right-header">
            {focusedDay
              ? `${formatDate(f(focusedDay, 'MenuDate'))} · ${mealtimeLabel(num(f(focusedDay, 'Mealtime')))}${str(f(focusedDay, 'MenuDisplayString')) ? ` · ${str(f(focusedDay, 'MenuDisplayString'))}` : ''}`
              : 'No day selected'}
          </div>
          <div className="aim-sm-right-body">
            <table className="aim-sm-grid">
              {/* Column layout — matches WinForms
                  frmDietech_AutoItemModification_SMChanges.Designer.vb
                  VisibleIndex order:
                    0. Old MP pin  (30px, IsOldMPItem)
                    1. Original Selective Menu text
                    2. Selected checkbox rendered as ✓ image (35px,
                       between the two panes — the WinForms
                       Tick_Grid pattern; click to toggle)
                    3. New Selective Menu text
                    4. New MP pin / slash (30px, IsNewMPItem or
                       NeverServeAdjustmentRemoved)
                  Push is a TOOLBAR button, not a per-row column,
                  matching WinForms `mbtnPushRight`. */}
              <colgroup>
                <col style={{ width: 30 }} />
                <col style={{ width: isReadOnly ? 'calc(50% - 30px)' : 'calc(50% - 47px)' }} />
                {!isReadOnly && <col style={{ width: 35 }} />}
                <col style={{ width: isReadOnly ? 'calc(50% - 30px)' : 'calc(50% - 48px)' }} />
                <col style={{ width: 30 }} />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Original Selective Menu</th>
                  {!isReadOnly && <th></th>}
                  <th>
                    {/* Header cell for the "New Selective Menu"
                        column. On the dashboard historical path
                        we render a lock icon at the right edge
                        when the focused day/mealtime has
                        ShowLock=true — that specific proposal is
                        stale/can't be applied (either menu date
                        is past OR a human saved another edit
                        after the auto-processing). Mirrors
                        WinForms `gvDetail_CustomDrawColumnHeader`
                        at frmDietech_AutoItemModification_
                        SMChanges.vb:1048-1066. */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ flex: 1 }}>New Selective Menu</span>
                      {focusedDayLocked && (
                        <Tooltip
                          title={
                            focusedDay && str(f(focusedDay, 'MenuDate'))
                              && new Date(str(f(focusedDay, 'MenuDate'))) < new Date(new Date().toDateString())
                              ? 'These items cannot be edited because the menu date has passed.'
                              : 'These items cannot be edited because they are not the most recent selective menu items.'
                          }
                          arrow
                        >
                          <i
                            className="bi bi-lock-fill"
                            aria-label="Read-only"
                            style={{ marginLeft: 'auto', fontSize: 16, color: 'var(--text-secondary, #64748b)', cursor: 'help' }}
                          />
                        </Tooltip>
                      )}
                    </span>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {focusedRows.map((row, i) => (
                  <SmProposalRow
                    key={i}
                    row={row}
                    focused={i === focusedRowInMeal}
                    hideSelectedCell={!!isReadOnly}
                    onFocus={() => setFocusedRowInMeal(i)}
                    onToggle={(v) => toggleSelected(i, v)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
      {/* Read-only mode (dashboard, every day/mealtime locked)
          hides Save entirely and renames Cancel to "OK",
          matching WinForms .vb:132-139. */}
      <DialogActionsBar
        sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}
        onCancel={handleCancel}
        cancelLabel={isReadOnly ? 'OK' : 'Cancel'}
        onSave={isReadOnly ? undefined : () => void handleSave()}
        savingLabel={activeResolveMutation.isPending ? 'Saving…' : 'Save'}
        saving={isPending}
        extra={(
          <>
            {/* Legend — WinForms `pictureLegend` anchored bottom-
                left at .Designer.vb:322-334 with a custom-drawn
                tooltip painted in `ToolTipController1_CustomDraw`
                (.vb:1016-1030). Colors here match WinForms:
                  • Green text  → net-new item on the new menu
                  • Yellow bg   → modified from original (kept +
                                  conflict)
                  • Gray text   → item removed from the new menu
                It rides in the bar's `extra`, left of Print / Cancel / Save. */}
            <Tooltip
              arrow
              placement="top-start"
              title={(
                <div style={{ padding: 4, lineHeight: 1.5, minWidth: 180 }}>
                  <div style={{ color: '#4ade80', fontWeight: 500 }}>Added on New Menu</div>
                  <div style={{ backgroundColor: '#f5c500', color: '#000', padding: '0 4px', fontWeight: 500 }}>
                    Modified from Original
                  </div>
                  <div style={{ color: '#cbd5e1', fontWeight: 500 }}>Removed from New Menu</div>
                </div>
              )}
            >
              <IconButton size="small" sx={{ p: 0.5 }} aria-label="Color legend">
                {/* Inline SVG legend icon — three colored bullets +
                    bars, one per tooltip line. Matches the WinForms
                    `pictureLegend` glyph (a bulleted list where each
                    bullet is one of the legend's colors). Colors
                    match the tooltip and the grid's color-coded
                    text/backgrounds. */}
                <svg
                  width="26"
                  height="24"
                  viewBox="0 0 26 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  {/* Row 1 — Added on New Menu (green) */}
                  <circle cx="4" cy="6" r="2.5" fill="#16a34a" />
                  <rect x="9" y="4.5" width="15" height="3" rx="1" fill="#16a34a" />
                  {/* Row 2 — Modified from Original (yellow bg) */}
                  <rect x="1" y="10.5" width="6" height="3" fill="#f5c500" />
                  <rect x="9" y="10.5" width="15" height="3" rx="1" fill="#000" opacity="0.55" />
                  {/* Row 3 — Removed from New Menu (gray) */}
                  <circle cx="4" cy="18" r="2.5" fill="#94a3b8" />
                  <rect x="9" y="16.5" width="15" height="3" rx="1" fill="#94a3b8" />
                </svg>
              </IconButton>
            </Tooltip>
            {/* Print: report 102 (AIM Selective Menu Changes) with the change set as it stands, AIM's proposal
                (result.combined, untouched by the review) and the run's context. A web print goes to the Linux
                renderer's fixed-layout generator. */}
            <Button
              variant="outlined"
              onClick={() => queueReport.mutate(smChangesReportRequest({
                facilityId,
                patientId,
                patientFullName,
                userId,
                dateFor: facilityToday(),
                dayMealtimes,
                combined,
                proposal: result?.combined ?? [],
                reportContext: result?.reportContext ?? null,
              }))}
              disabled={isPending || queueReport.isPending || dayMealtimes.length === 0}
              startIcon={<i className="bi bi-printer" aria-hidden="true" />}
              sx={SECONDARY_BUTTON_SX}
            >
              Print
            </Button>
          </>
        )}
      />
      </>
      )}
    </Dialog>
    {/* Step 1 — mirrors WinForms `dmYesNoCancel` at
        frmDietech_AutoItemModification_SMChanges.vb:677.
        Three buttons: Yes = save, No = advance to revert
        prompt, Cancel = stay on the SM dialog.
        `ConfirmDialog` is 2-button-only, so this is inline. */}
    <Dialog
      open={cancelPrompt === 'save'}
      onClose={resolveMutation.isPending ? undefined : handleCancelSavePrompt}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <HelpOutlineIcon sx={{ color: 'var(--primary-blue)', fontSize: 24 }} />
        Save Changes
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          The selective menu has not been saved.  Do you wish to save the new selective menus (right column)?
        </Typography>
      </DialogContent>
      {/* Three outcomes: No (move on to the revert prompt) at the far
          left, then Cancel, then Yes (save) at the far right. */}
      <DialogActionsBar
        onCancel={handleCancelSavePrompt}
        onSave={handleSaveFromPrompt}
        saveLabel="Yes"
        savingLabel="Working..."
        saving={resolveMutation.isPending}
        leftSlot={(
          <Button
            variant="outlined"
            sx={SECONDARY_BUTTON_SX}
            onClick={handleDiscardFromSavePrompt}
            disabled={resolveMutation.isPending}
          >
            No
          </Button>
        )}
      />
    </Dialog>
    {/* Step 2 — mirrors WinForms `dmQuestion_DFB2` at
        frmDietech_AutoItemModification_SMChanges.vb:685.
        Yes/No only: Yes = re-insert originals (SaveOldValues),
        No = stay on SM dialog. */}
    <ConfirmDialog
      open={cancelPrompt === 'revert'}
      onClose={handleCancelRevertPrompt}
      onConfirm={handleRevertFromPrompt}
      title="Saving Changes"
      message="Are you absolutely sure you want to revert to the original saved selective menu choices (left column) and disregard the updated selective menus (right column)?"
      confirmLabel="Yes"
      cancelLabel="No"
      variant="warning"
      isPending={revertMutation.isPending}
    />
    </>
  );
}

// Compare two dictionary rows by their composite key. Uses the
// case-insensitive accessor so rows shipped from the API
// (first-char-lower keys) compare identically to rows we cloned
// or mutated locally.
const SAME_ROW_KEYS = [
  'Day', 'Mealtime', 'FKey_Prd_Menu_Category', 'Position_New',
  'Itm_Descr_New', 'FKey_Prd_Selective_Menu', 'FKey_Prd_Menu_Detail',
];
function sameRow(a: AimSMRowDict, b: AimSMRowDict): boolean {
  return SAME_ROW_KEYS.every((k) => f(a, k) === f(b, k));
}

function formatDate(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

function SmProposalRow({ row, focused, hideSelectedCell, onFocus, onToggle }: {
  row: AimSMRowDict;
  focused: boolean;
  hideSelectedCell?: boolean;
  onFocus: () => void;
  onToggle: (v: boolean) => void;
}) {
  const posOld = num(f(row, 'Position_Old')) ?? 0;
  const posNew = num(f(row, 'Position_New')) ?? 0;
  const oldDescr = str(f(row, 'Itm_Descr_Old'));
  const newDescr = str(f(row, 'Itm_Descr_New'));
  const selected = bool(f(row, 'Selected'));
  const highlight = bool(f(row, 'HighlightNewItem'));
  const highlightSelectedOnly = bool(f(row, 'HighlightNewItemOnlySelected'));
  const isOldMP = bool(f(row, 'IsOldMPItem'));
  const isNewMP = bool(f(row, 'IsNewMPItem'));
  const neverServe = bool(f(row, 'NeverServeAdjustmentRemoved'));

  // New-cell text styling — matches WinForms
  // gvDetail_CustomDrawCell branches at .vb:360-378 for the
  // colItm_Descr_New column. The Old-side column has no color
  // rules in WinForms — only bold on Position=1 and indent.
  const newTextStyle: React.CSSProperties = {};
  if (!selected && posNew > 1 && (f(row, 'FKey_Prd_Menu_Detail') == null || oldDescr !== '')) {
    newTextStyle.color = '#6b7280';   // gray unselected child
  }
  if (highlight) {
    if (oldDescr === '') newTextStyle.color = '#16a34a';      // net-new item → green
    else if (selected) newTextStyle.backgroundColor = 'rgba(255, 235, 130, 0.55)';   // conflict + kept → yellow bg
  }
  if (highlightSelectedOnly && selected) {
    newTextStyle.color = '#16a34a';
  }

  const oldCellStyle: React.CSSProperties = { fontWeight: posOld === 1 ? 700 : 400, paddingLeft: posOld > 1 ? (posOld - 1) * 16 : 0 };
  const newCellStyle: React.CSSProperties = { fontWeight: posNew === 1 ? 700 : 400, paddingLeft: posNew > 1 ? (posNew - 1) * 16 : 0, ...newTextStyle };

  // Only interactive on rows where Selected is meaningful
  // (Position_New > 1 — Position=1 rows are category headers,
  // toggled indirectly via the cascade).
  const selectedInteractive = posNew > 1;

  const rowClassName = [
    posOld === 1 || posNew === 1 ? 'aim-sm-row-cat' : '',
    focused ? 'aim-sm-row-focused' : '',
  ].filter(Boolean).join(' ');

  return (
    <tr className={rowClassName} onClick={onFocus}>
      {/* Col 0 — OLD MP pin (WinForms colMPOld, IsOldMPItem) */}
      <td className="aim-sm-icon-cell">
        {isOldMP && (
          <Tooltip title="This item was on the resident's meal pattern before the diet change">
            <i className="bi bi-pin-angle-fill" style={{ color: '#dc2626', fontSize: 14 }} />
          </Tooltip>
        )}
      </td>
      {/* Col 1 — Original text */}
      <td style={oldCellStyle}>{oldDescr}</td>
      {/* Col 2 — Selected (rendered as ✓ image when true,
          blank when false). Click toggles. Mirrors WinForms
          `colNew_Selected` (VisibleIndex=1, ImageChecked =
          Tick_Grid.png). Hidden entirely in read-only mode
          (matches WinForms `colNew_Selected.Visible = False`
          at .vb:136). */}
      {!hideSelectedCell && (
      <td
        className={`aim-sm-check-cell aim-sm-selected-cell${selectedInteractive ? '' : ' aim-sm-selected-cell-disabled'}`}
        onClick={(e) => {
          if (!selectedInteractive) return;
          e.stopPropagation();
          onFocus();
          onToggle(!selected);
        }}
        role={selectedInteractive ? 'button' : undefined}
        aria-pressed={selectedInteractive ? selected : undefined}
        aria-label={selectedInteractive ? (selected ? 'Selected, click to unselect' : 'Not selected, click to select') : undefined}
      >
        {/* Icon is ALWAYS mounted at fixed dimensions; only its
            visibility toggles. Rendering the <i> conditionally
            (or wrapping it in a variable-content span) still
            let Bootstrap-Icons font-metric quirks shift the row
            height between checked and unchecked states. With
            visibility:hidden the box model is byte-identical
            either way. */}
        <i
          className="bi bi-check2"
          style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            fontSize: 16,
            lineHeight: '16px',
            color: '#2563eb',
            visibility: selected ? 'visible' : 'hidden',
          }}
        />
      </td>
      )}
      {/* Col 3 — New text */}
      <td style={newCellStyle}>{newDescr}</td>
      {/* Col 4 — NEW MP pin (colMPNew): Pushpin-Red on
          IsNewMPItem, warning-icon on NeverServeAdjustmentRemoved.
          Mutually exclusive per WinForms .vb:381-387. */}
      <td className="aim-sm-icon-cell">
        {isNewMP && (
          <Tooltip title="This item is on the resident's meal pattern">
            <i className="bi bi-pin-angle-fill" style={{ color: '#dc2626', fontSize: 14 }} />
          </Tooltip>
        )}
        {!isNewMP && neverServe && (
          <Tooltip title="Adjustment caused item to be unchecked">
            <i className="bi bi-slash-circle" style={{ color: '#d97706', fontSize: 14 }} />
          </Tooltip>
        )}
      </td>
    </tr>
  );
}
